import React, { Component } from 'react'
import { arrayOf, string } from 'prop-types'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import config from '../../config'

var COLORS = [
  '#009d3d', '#2e7d32', '#43a047', '#66bb6a', '#81c784',
  '#4caf50', '#388e3c', '#1b5e20', '#a5d6a7', '#00695c'
]

// Stable identity of a zip-code list, so a re-render that rebuilds the array
// (same values, new reference) does not re-trigger a full map reload.
function zipKey (zipCodes) {
  return (zipCodes || []).join(',')
}

// Module-level cache of the commune contours: the geo API returns the same
// polygons for a given zip code, so fetch each one at most once per session.
var CONTOUR_CACHE = {}
function fetchContour (zip) {
  if (!CONTOUR_CACHE[zip]) {
    var url = 'https://geo.api.gouv.fr/communes?codePostal=' + zip + '&fields=nom,contour,centre&format=json'
    CONTOUR_CACHE[zip] = window.fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status)
        return response.json()
      })
      .catch(function (error) {
        // Do not cache a failure: allow a later retry.
        delete CONTOUR_CACHE[zip]
        throw error
      })
  }
  return CONTOUR_CACHE[zip]
}

class ZoneMap extends Component {
  constructor (props) {
    super(props)
    this.mapRef = null
    this.map = null
    this.setMapRef = this.setMapRef.bind(this)
  }

  setMapRef (el) {
    this.mapRef = el
  }

  get isUnmounted () {
    return this._unmounted
  }

  componentDidMount () {
    if (this.mapRef) {
      this.initMap()
    }
  }

  componentDidUpdate (prevProps) {
    // Compare by content, not by array reference: a parent re-render rebuilds the
    // zipCodes array with the same values, which must NOT reload the whole map.
    if (zipKey(prevProps.zipCodes) !== zipKey(this.props.zipCodes)) {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      this.initMap()
    }
  }

  componentWillUnmount () {
    this._unmounted = true
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap () {
    var zipCodes = this.props.zipCodes || []
    if (zipCodes.length === 0 || !this.mapRef) return

    // Create map with no zoom controls for clean look
    this.map = L.map(this.mapRef, {
      zoomControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false
    }).setView([46.8, 2.3], 6)

    // CARTO exige desormais une cle : sans elle les tuiles sont filigranees.
    // L'attribution CARTO + OpenStreetMap est imposee par leurs conditions d'usage.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png?key=' + config['carto-key'], {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(this.map)

    this.loadZones(zipCodes)
  }

  loadZones (zipCodes) {
    var map = this.map
    var allBounds = []
    var pending = zipCodes.length

    var self = this

    zipCodes.forEach(function (zip, index) {
      var color = COLORS[index % COLORS.length]

      fetchContour(zip)
        .then(function (communes) {
          // Drop the result if the component is gone or the map was rebuilt
          // (new zip codes) while this request was in flight.
          if (self.isUnmounted || self.map !== map) return
          communes.forEach(function (commune) {
            if (commune.contour) {
              var geoJson = L.geoJSON(commune.contour, {
                style: {
                  fillColor: color,
                  fillOpacity: 0.25,
                  color: color,
                  weight: 2,
                  opacity: 0.8
                }
              }).addTo(map)

              allBounds.push(geoJson.getBounds())
            }
          })

          pending--
          // Fit bounds once all zips are loaded
          if (pending === 0 && allBounds.length > 0 && map && map.getContainer()) {
            var combined = allBounds[0]
            for (var j = 1; j < allBounds.length; j++) {
              combined = combined.extend(allBounds[j])
            }
            map.fitBounds(combined, { padding: [25, 25], maxZoom: 13 })
          }
        })
        .catch(function () {
          pending--
        })
    })
  }

  render () {
    return (
      <div
        ref={this.setMapRef}
        style={{
          width: '100%',
          height: '200px',
          borderRadius: '8px',
          border: '1px solid #EBEBEB',
          overflow: 'hidden'
        }}
      />
    )
  }
}

ZoneMap.propTypes = {
  zipCodes: arrayOf(string)
}

export default ZoneMap



// WEBPACK FOOTER //
// ./src/components/gardeners/ZoneMap.js